import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FiList, FiPlus, FiCheck, FiChevronRight } from 'react-icons/fi';
import { getTodos, toggleTodoComplete, createTodo, getTeamMembers } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

function formatDueLabel(dateStr) {
  if (!dateStr) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr + 'T00:00:00');
  const diffDays = Math.round((d - today) / 86400000);
  if (diffDays === 0) return { label: 'Today', color: '#c2410c', bg: '#fff7ed' };
  if (diffDays < 0) return { label: `${Math.abs(diffDays)}d overdue`, color: '#b91c1c', bg: '#fef2f2' };
  if (diffDays === 1) return { label: 'Tomorrow', color: '#1d4ed8', bg: '#eff6ff' };
  if (diffDays < 7) return { label: d.toLocaleDateString('en-US', { weekday: 'short' }), color: '#1d4ed8', bg: '#eff6ff' };
  return { label: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), color: '#4b5563', bg: '#f3f4f6' };
}

export default function TodoWidget() {
  const { user } = useAuth();
  const isManager = ['Admin', 'Owner', 'Office Manager'].includes(user?.role);
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quickText, setQuickText] = useState('');
  const [creating, setCreating] = useState(false);
  const [team, setTeam] = useState([]);
  const [assignTo, setAssignTo] = useState(''); // '' = me

  useEffect(() => {
    if (!isManager) return;
    getTeamMembers().then(r => setTeam((r.data || []).filter(m => m.is_active))).catch(() => {});
  }, [isManager]);

  const load = useCallback(async () => {
    try {
      const res = await getTodos({ show: 'open' });
      setTodos(res.data.todos || []);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleToggle = async (todo) => {
    // optimistic
    setTodos(t => t.filter(x => x.id !== todo.id));
    try {
      await toggleTodoComplete(todo.id, true);
    } catch (e) {
      load();
    }
  };

  const handleQuickAdd = async (e) => {
    e.preventDefault();
    const text = quickText.trim();
    if (!text) return;
    setCreating(true);
    try {
      const payload = { text };
      if (isManager && assignTo && Number(assignTo) !== user?.id) payload.owner_id = Number(assignTo);
      await createTodo(payload);
      setQuickText('');
      setAssignTo('');
      load();
    } catch (err) {
      // silent
    } finally {
      setCreating(false);
    }
  };

  // Sort: overdue first, then today, then by due date, then no-due
  const sorted = [...todos].sort((a, b) => {
    const ad = a.due_date ? new Date(a.due_date + 'T00:00:00').getTime() : Infinity;
    const bd = b.due_date ? new Date(b.due_date + 'T00:00:00').getTime() : Infinity;
    return ad - bd;
  });
  const visible = sorted.slice(0, 5);
  const remaining = sorted.length - visible.length;

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '15px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px'
      }}>
        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FiList size={18} /> My To-Do
        </h2>
        <Link to="/todos" style={{
          color: '#2563eb', textDecoration: 'none', fontSize: '14px',
          display: 'flex', alignItems: 'center', gap: '2px'
        }}>
          View all <FiChevronRight size={14} />
        </Link>
      </div>

      <form onSubmit={handleQuickAdd} style={{ display: 'flex', gap: '6px', marginBottom: '10px', flexWrap: 'wrap' }}>
        <input
          type="text"
          value={quickText}
          onChange={(e) => setQuickText(e.target.value)}
          placeholder="Quick add a task…"
          disabled={creating}
          maxLength={500}
          style={{
            flex: 1, minWidth: '140px', padding: '8px 10px', fontSize: '14px',
            border: '1px solid #e5e7eb', borderRadius: '8px', outline: 'none',
          }}
        />
        {isManager && team.length > 0 && (
          <select
            value={assignTo}
            onChange={(e) => setAssignTo(e.target.value)}
            title="Assign to"
            style={{
              padding: '8px 10px', fontSize: '13px', border: '1px solid #e5e7eb',
              borderRadius: '8px', outline: 'none', background: 'white', maxWidth: '140px',
            }}
          >
            <option value="">Me</option>
            {team.filter(m => m.id !== user?.id).map(m => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
        )}
        <button
          type="submit"
          disabled={!quickText.trim() || creating}
          aria-label="Add task"
          style={{
            padding: '8px 12px',
            backgroundColor: quickText.trim() ? '#2563eb' : '#e5e7eb',
            color: 'white', border: 'none', borderRadius: '8px',
            cursor: quickText.trim() ? 'pointer' : 'not-allowed',
            display: 'flex', alignItems: 'center'
          }}
        >
          <FiPlus size={16} />
        </button>
      </form>

      {loading ? (
        <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>Loading…</div>
      ) : sorted.length === 0 ? (
        <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
          Nothing on your list. Nicely done.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {visible.map(t => {
            const due = formatDueLabel(t.due_date);
            return (
              <div key={t.id} style={{
                display: 'flex', alignItems: 'flex-start', gap: '10px',
                padding: '8px 10px', backgroundColor: '#f9fafb', borderRadius: '8px',
                border: '1px solid #f3f4f6',
              }}>
                <button
                  onClick={() => handleToggle(t)}
                  aria-label="Mark complete"
                  style={{
                    flexShrink: 0, marginTop: '1px',
                    width: '20px', height: '20px', borderRadius: '5px',
                    border: '2px solid #d1d5db', backgroundColor: 'white',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}
                >
                  <FiCheck size={12} color="transparent" />
                </button>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', color: '#111827', wordBreak: 'break-word' }}>
                    {t.text}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '3px' }}>
                    {due && (
                      <span style={{
                        fontSize: '10px', fontWeight: 600,
                        padding: '1px 6px', borderRadius: '999px',
                        backgroundColor: due.bg, color: due.color,
                      }}>{due.label}</span>
                    )}
                    {t.assigned_by_name && (
                      <span style={{ fontSize: '10px', color: '#9ca3af' }}>from {t.assigned_by_name}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {remaining > 0 && (
            <Link to="/todos" style={{
              fontSize: '12px', color: '#6b7280', textAlign: 'center', textDecoration: 'none',
              padding: '4px'
            }}>
              + {remaining} more
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
